import _ from 'lodash';
import log from 'loglevel';
import { defineStore } from 'pinia';
import { User } from '@/interfaces/User';
import { UserMetrics } from '@/interfaces/UserMetrics';
import { UserService } from '@/api/services/UserService';
import { MyReferralResponse } from '@/interfaces/MyReferralResponse';
import { VerifyPhoneNumberInput } from '@/interfaces/InHouseTasks/VerifyPhoneNumberInput';

// Nick Name Generator
import { Config } from '@/utils/UniqueNameGenerator/unique-names-generator.constructor';
import { uniqueNamesGenerator } from '@/utils/UniqueNameGenerator/unique-names-generator';
import { adjectives, animals, colors } from '@/utils/UniqueNameGenerator/dictionaries/index';


export const useUserStore = defineStore({
    id: 'userStore',
    state: () => ({
        user: {} as User,
        userMetrics: {} as UserMetrics,

        // Referrals
        myReferrals: [] as User[],
        loadingMyReferrals: false,
        myReferralsPaging: {
            count: 25,
            offset: 0,
            page: 1,
            totalPages: 1,
        },
        referralCode: '' as string,
    }),
    actions: {
        async initialize() {
            try {
                await this.getUser();
                await this.fetchMyReferrals();
                return true;
            } catch (error) {
                return false;
            }
        },

        async getUser() {
            try {
                const user: User = await UserService.getUser();

                const config: Config = {
                    dictionaries: [adjectives, colors, animals],
                    separator: ' ',
                    seed: user.id,
                    style: 'capital',
                    length: 3
                };
                user.nick_name = uniqueNamesGenerator(config);
                this.syncUser(user);

            } catch (error) {
                log.error('Failed to fetch or sync user:', error);
            }
        },

        syncUser(newUser: Partial<User>) {
            _.merge(this.user, newUser);
        },

        // Phone and PayPal methods
        setPhoneNumber(phoneNumber: string) {
            this.user.phone_number = phoneNumber;
        },

        setPhoneISOCode(phoneISOCode: string) {
            this.user.phone_iso_code = phoneISOCode;
        },

        setPhoneCountryCode(phoneCountryCode: string) {
            this.user.phone_country_code = phoneCountryCode;
        },

        setPaypalEmail(paypalEmail: string) {
            this.user.paypal_email = paypalEmail;
        },

        async updateUserPhoneNumber() {
            const data = {
                phone_number: this.user.phone_number,
                phone_iso_code: this.user.phone_iso_code,
                phone_country_code: this.user.phone_country_code
            } as VerifyPhoneNumberInput;
            await UserService.updateUserPhoneNumber(data);
        },

        async updateUserPaypalEmail() {
            const data = {
                paypal_email: this.user.paypal_email
            };
            await UserService.updateUserPaypalEmail(data);
        },

        async updateUserProfile(phoneNumber: string, phoneISOCode: string, phoneCountryCode: string, paypalEmail: string) {
            const phoneChanged = this.user.phone_number !== phoneNumber ||
                this.user.phone_iso_code !== phoneISOCode ||
                this.user.phone_country_code !== phoneCountryCode;

            const paypalChanged = this.user.paypal_email !== paypalEmail;

            this.setPhoneNumber(phoneNumber);
            this.setPhoneISOCode(phoneISOCode);
            this.setPhoneCountryCode(phoneCountryCode);
            this.setPaypalEmail(paypalEmail);

            if (phoneChanged) {
                await this.updateUserPhoneNumber();
            }

            if (paypalChanged) {
                await this.updateUserPaypalEmail();
            }
        },

        // Referral methods
        async fetchMyReferrals() {
            this.loadingMyReferrals = true;
            const myReferrals: MyReferralResponse = await UserService.getMyReferrals();
            this.myReferrals = myReferrals.data;
            this.myReferralsPaging.totalPages = myReferrals.paging.totalPages;
            this.loadingMyReferrals = false;
        },

        setPage(page: number) {
            this.myReferralsPaging.page = page;
        },

        setReferralCode(referralCode: string) {
            this.referralCode = referralCode;
        },

        removeReferralCode() {
            this.referralCode = '';
        },

        clearStoreData() {
            this.$reset();
        }
    }
}); 